import { useEffect, useMemo, useRef, useState } from 'react';
import { globalBus } from '../streams/wsBus';
import { useActionFeedback } from '../streams/useActionFeedback';

export function HiveCtlBridge() {
  const [lines, setLines] = useState<string[]>([]);
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const { states, fire } = useActionFeedback(globalBus);

  useEffect(() => {
    const unsubscribe = globalBus.subscribe((message) => {
      if (message.t === '__status') {
        setStatus(message.status);
      }
      if (message.t === 'scene' && message.id === 'hivecore.hivectl') {
        setLines(message.body?.widgets?.find((w: any) => w.key === 'hivectl.log')?.lines ?? []);
      }
    });
    globalBus.sendIntent('scene.request', { id: 'hivecore.hivectl' });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines]);

  const connectionChip = useMemo(() => {
    switch (status) {
      case 'connected': return { label: 'Connected', tone: 'ok' };
      case 'disconnected': return { label: 'Reconnecting…', tone: 'warn' };
      default: return { label: 'Connecting…', tone: 'warn' };
    }
  }, [status]);

  const runCommand = (cmd: string) => fire(`hivectl.${cmd}`, 'palette.invoke', { cmd });

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <header className="panel-header">
        <h2>HiveCtl Bridge</h2>
        <span className={`chip ${connectionChip.tone}`}>{connectionChip.label}</span>
        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
          <button data-state={states['hivectl.fix.soma'] ?? 'idle'} onClick={() => runCommand('fix.soma')}>Fix SoMA</button>
          <button data-state={states['hivectl.build.reload'] ?? 'idle'} onClick={() => runCommand('build.reload')}>Reload Build</button>
          <button data-state={states['hivectl.agents.resumeAll'] ?? 'idle'} onClick={() => runCommand('agents.resumeAll')}>Resume All</button>
          <button data-state={states['hivectl.agents.pauseAll'] ?? 'idle'} onClick={() => runCommand('agents.pauseAll')}>Pause All</button>
        </div>
      </header>
      <div
        ref={scrollRef}
        className="terminal-log"
        role="log"
        aria-live="polite"
      >
        {lines.length === 0 ? (
          <p style={{ color: 'var(--muted)' }}>No output yet. Run a quick command above.</p>
        ) : (
          lines.map((line, idx) => <div key={`${idx}-${line}`}>{line}</div>)
        )}
      </div>
    </div>
  );
}
